import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LayersControl, LayerGroup, useMap } from 'react-leaflet';
import MaplibreLayer from './MaplibreLayer';
import { getVectorLayersConfig, getVectorLayersFromCatalog } from '../../config/vector-layers';
import { PMTiles } from 'pmtiles';
import { propertyApi } from '../../services/BaseApiService';

function buildStyleForLayer(cfg, getStyleForTheme, resolvedSourceLayer, resolvedZooms) {
  const _DEBUG = import.meta && import.meta.env && import.meta.env.VITE_DEBUG === 'true';
  const geometryType = cfg?.geometryType || 'polygon';
  // Valores base vindos da configuração
  let fillColor = (cfg.style && cfg.style.fillColor) || '#00897B';
  let fillOpacity = cfg.style && cfg.style.fillOpacity != null ? cfg.style.fillOpacity : 0.35;
  let lineColor = (cfg.style && cfg.style.color) || '#004D40';
  let lineWidth = cfg.style && cfg.style.weight != null ? cfg.style.weight : 1;
  let lineOpacity = cfg.style && cfg.style.opacity != null ? cfg.style.opacity : 1;
  let circleRadius = cfg.style && cfg.style.radius != null ? cfg.style.radius : 5;
  let circleStrokeColor = (cfg.style && cfg.style.strokeColor) || lineColor;
  let circleStrokeWidth =
    cfg.style && cfg.style.strokeWidth != null ? cfg.style.strokeWidth : Math.max(lineWidth, 1);
  let circleStrokeOpacity =
    cfg.style && cfg.style.strokeOpacity != null ? cfg.style.strokeOpacity : lineOpacity;
  let lineDashArray = undefined; // ex: '6 6' → [6,6]

  // Se vier um resolvedor de estilo (do layer-style-config), aplicar sobrescritas
  if (typeof getStyleForTheme === 'function' && (cfg?.styleKey || cfg?.name)) {
    try {
      const s = getStyleForTheme(cfg.styleKey || cfg.name);
      if (s) {
        if (s.fillColor) fillColor = s.fillColor;
        if (typeof s.fillOpacity === 'number') fillOpacity = s.fillOpacity;
        if (s.color) lineColor = s.color;
        if (typeof s.weight === 'number') lineWidth = s.weight;
        if (typeof s.opacity === 'number') lineOpacity = s.opacity;
        if (typeof s.radius === 'number') circleRadius = s.radius;
        if (s.strokeColor) circleStrokeColor = s.strokeColor;
        if (typeof s.strokeWidth === 'number') circleStrokeWidth = s.strokeWidth;
        if (typeof s.strokeOpacity === 'number') circleStrokeOpacity = s.strokeOpacity;
        if (s.dashArray && typeof s.dashArray === 'string') lineDashArray = s.dashArray;
        // Observação: getStyleForTheme retorna chaves pensadas para Leaflet; mapeamos para MapLibre aqui
      }
    } catch (_) {
      /* no-op */
    }
  }

  // Em modo DEBUG, não sobrescrever estilos do usuário.
  // Mantemos apenas a camada opcional de background (abaixo) e logs para inspeção.

  const sourceLayer = resolvedSourceLayer || cfg.sourceLayer || cfg.id;
  const minzoom =
    resolvedZooms && Number.isFinite(resolvedZooms.min) ? resolvedZooms.min : (cfg.minZoom ?? 0);
  const maxzoom =
    resolvedZooms && Number.isFinite(resolvedZooms.max)
      ? resolvedZooms.max + 1
      : (cfg.maxZoom ?? 24);

  const geometryLayers =
    geometryType === 'point'
      ? [
          {
            id: `${cfg.id}-circle`,
            type: 'circle',
            source: cfg.id,
            'source-layer': sourceLayer,
            minzoom,
            maxzoom,
            paint: {
              'circle-color': fillColor,
              'circle-opacity': fillOpacity,
              'circle-radius': circleRadius,
              'circle-stroke-color': circleStrokeColor,
              'circle-stroke-width': circleStrokeWidth,
              'circle-stroke-opacity': circleStrokeOpacity,
            },
          },
        ]
      : [
          {
            id: `${cfg.id}-fill`,
            type: 'fill',
            source: cfg.id,
            'source-layer': sourceLayer,
            minzoom,
            maxzoom,
            paint: {
              'fill-color': fillColor,
              'fill-opacity': fillOpacity,
              'fill-outline-color': lineColor,
            },
          },
          {
            id: `${cfg.id}-line`,
            type: 'line',
            source: cfg.id,
            'source-layer': sourceLayer,
            minzoom,
            maxzoom,
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': lineColor,
              'line-width': lineWidth,
              'line-opacity': lineOpacity,
              ...(lineDashArray && /^\d+(\s+\d+)*$/.test(lineDashArray)
                ? {
                    'line-dasharray': lineDashArray
                      .split(/\s+/)
                      .map((n) => Number(n))
                      .filter((n) => !Number.isNaN(n)),
                  }
                : {}),
            },
          },
        ];

  // MapLibre style JSON com source em PMTiles e layers conforme tipo geométrico
  const style = {
    version: 8,
    sources: {
      [cfg.id]: {
        type: 'vector',
        // PMTiles protocol handler: pmtiles://<http(s)://.../file.pmtiles>
        url: `pmtiles://${cfg.url}`,
        attribution: '© Dados públicos — via GN Tiles',
      },
    },
    layers: [
      // Camada de background transparente para permitir overlay entre múltiplas instâncias
      {
        id: `${cfg.id}-bg`,
        type: 'background',
        paint: { 'background-color': 'rgba(0,0,0,0)' },
      },
      ...geometryLayers,
    ],
  };
  //  if (DEBUG) {
  //    try {
  //      console.debug('[VectorTileLayers] style built for', cfg.name, {
  //        fillColor, fillOpacity, lineColor, lineWidth, lineOpacity, lineDashArray, resolvedSourceLayer
  //      });
  //    } catch (_) {}
  //  }
  return style;
}

export default function VectorTileLayers({ getStyleForTheme }) {
  const [layers, setLayers] = useState(() => getVectorLayersConfig());

  useEffect(() => {
    let cancelled = false;

    async function loadDynamicCatalog() {
      try {
        const response = await propertyApi.get('/api/v1/tiles/catalog');
        const items = Array.isArray(response?.data?.items) ? response.data.items : [];
        const dynamicLayers = getVectorLayersFromCatalog(items);

        if (!cancelled && dynamicLayers.length > 0) {
          setLayers(dynamicLayers);
        }
      } catch (_error) {
        if (!cancelled) {
          setLayers(getVectorLayersConfig());
        }
      }
    }

    loadDynamicCatalog();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      {layers.map((cfg) => (
        <VectorOverlayItem key={cfg.id} cfg={cfg} getStyleForTheme={getStyleForTheme} />
      ))}
    </>
  );
}

function VectorOverlayItem({ cfg, getStyleForTheme }) {
  const map = useMap();
  const groupRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [resolvedSource, setResolvedSource] = useState(null);
  const [resolvedZooms, setResolvedZooms] = useState(null); // { min: number, max: number }

  // Resolve source-layer quando o overlay for ligado pela primeira vez
  useEffect(() => {
    let cancelled = false;
    async function resolveSourceLayer() {
      try {
        if (!visible) return;
        // Já resolvido
        // Permitimos reprocessar zooms mesmo se source já resolvido, caso mude URL
        // Busca metadata do PMTiles
        const pm = new PMTiles(cfg.url);
        const md = await pm.getMetadata();
        let names = [];
        let metaMin = null;
        let metaMax = null;
        if (md) {
          if (Array.isArray(md.vector_layers)) {
            names = md.vector_layers.map((l) => l.id || l.name).filter(Boolean);
          } else if (Array.isArray(md.layers)) {
            names = md.layers.map((l) => l.id || l.name).filter(Boolean);
          } else if (md.json && typeof md.json === 'string') {
            try {
              const parsed = JSON.parse(md.json);
              if (Array.isArray(parsed.vector_layers)) {
                names = parsed.vector_layers.map((l) => l.id || l.name).filter(Boolean);
              }
            } catch (_) {
              /* no-op */
            }
          }
          // minzoom/maxzoom podem vir em md diretamente (TileJSON)
          try {
            if (md.minzoom != null && !Number.isNaN(Number(md.minzoom)))
              metaMin = Number(md.minzoom);
            if (md.maxzoom != null && !Number.isNaN(Number(md.maxzoom)))
              metaMax = Number(md.maxzoom);
          } catch (_) {
            /* no-op */
          }
        }
        let chosen = null;
        if (cfg.sourceLayer && names.includes(cfg.sourceLayer)) {
          chosen = cfg.sourceLayer;
        } else if (names.length > 0) {
          chosen = names[0];
        } else {
          chosen = cfg.sourceLayer || cfg.id; // fallback seguro
        }
        if (!cancelled) {
          setResolvedSource(chosen);
          setResolvedZooms(
            metaMin != null || metaMax != null
              ? { min: metaMin ?? undefined, max: metaMax ?? undefined }
              : null,
          );
        }
      } catch (_e) {
        /* metadata fetch failed — use fallback */
        if (!cancelled) {
          setResolvedSource(cfg.sourceLayer || cfg.id);
          setResolvedZooms(null);
        }
      }
    }
    resolveSourceLayer();
    return () => {
      cancelled = true;
    };
  }, [visible, cfg.url, cfg.sourceLayer, cfg.id]);

  useEffect(() => {
    // Estado inicial após montar o LayerGroup
    const init = () => {
      try {
        if (groupRef.current) setVisible(map.hasLayer(groupRef.current));
      } catch (_) {
        /* no-op */
      }
    };
    init();
    // Alterna visibilidade conforme o Overlay é ligado/desligado
    const onOverlayAdd = (e) => {
      try {
        if (groupRef.current && e.layer === groupRef.current) setVisible(true);
      } catch (_) {
        /* no-op */
      }
    };
    const onOverlayRemove = (e) => {
      try {
        if (groupRef.current && e.layer === groupRef.current) setVisible(false);
      } catch (_) {
        /* no-op */
      }
    };
    map.on('overlayadd', onOverlayAdd);
    map.on('overlayremove', onOverlayRemove);
    return () => {
      try {
        map.off('overlayadd', onOverlayAdd);
        map.off('overlayremove', onOverlayRemove);
      } catch (_) {
        /* no-op */
      }
    };
  }, [map]);

  const style = useMemo(
    () => buildStyleForLayer(cfg, getStyleForTheme, resolvedSource, resolvedZooms),
    [cfg, getStyleForTheme, resolvedSource, resolvedZooms],
  );
  return (
    <LayersControl.Overlay name={cfg.name} checked={false}>
      <LayerGroup ref={groupRef}>
        {visible && resolvedSource && (
          <MaplibreLayer
            style={style}
            zIndex={cfg.zIndex || 700}
            interactiveLayerId={
              cfg.geometryType === 'point' ? `${cfg.id}-circle` : `${cfg.id}-fill`
            }
            popupFields={cfg.popupFields || []}
            displayName={cfg.name}
            controllerRef={groupRef}
          />
        )}
      </LayerGroup>
    </LayersControl.Overlay>
  );
}
